import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useEndpoints } from "../../../api/ApiContext";
import { ApiError } from "../../../api/errors";
import type {
  ApiKeyDisposition,
  ChangeRoleResponse,
  OrgInvitation,
  OrgMember,
  OrgRole,
  OwnershipTransferResponse,
  RemoveMemberResponse,
  RoleRequest,
} from "../../../api/types";
import { useSession } from "../../../auth/AuthProvider";
import { homeFor } from "../../../auth/pages";
import { CommandError } from "../../../components/CommandError";
import { RejectionDetail } from "../../../components/RejectionDetail";
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  EmptyState,
  Input,
  PageHeader,
  QueryView,
  RadioGroup,
  Select,
  StatusPill,
  Table,
} from "../../../components/ui";
import { formatLocal } from "../../../lib/datetime";
import { useIntentKey } from "../../../lib/useIntentKey";
import {
  isFuture,
  isManager,
  isPrivileged,
  OrgTabs,
  PRIVILEGED_ROLE_TEXT,
  ROLE_DESCRIPTION,
  ROLE_LABEL,
  ROLES,
  SINGLE_USER_FALLBACK_TEXT,
} from "./orgCopy";

// Organization → Members (G-06; UX-DESIGN.md §4.2). Routes: org.go:44-52
// (migration 0062). The authority rules live in the SQL functions; this
// screen offers only what the caller's roles allow, and still explains
// every refusal the server sends (403 insufficient_role, 422 last_owner,
// approval_quorum_would_break, ...).

const roleOptions = ROLES.map((r) => ({ value: r, label: `${ROLE_LABEL[r]}${isPrivileged(r) ? " (privileged)" : ""}` }));

function roleLabel(r: OrgRole | null): string {
  return r ? ROLE_LABEL[r] : "No role";
}

/** A 202 dual_control_pending result, in words (§4.3). */
export function PendingRequestNote({ request, subject }: { request: RoleRequest; subject: string }) {
  return (
    <div className="stack-sm">
      <span>
        {request.action_type === "ownership_transfer" ? `Transferring ownership to ${subject}` : `The role change for ${subject}`} is
        pending. It needs {request.single_user_fallback ? "no second approver" : "a second owner or admin to approve it"} and takes
        effect no earlier than {formatLocal(request.effective_at)}.
      </span>
      {request.single_user_fallback ? <span>{SINGLE_USER_FALLBACK_TEXT}</span> : null}
    </div>
  );
}

function AuthorityCell({ m, now }: { m: OrgMember; now: number }) {
  const privileged = m.roles.filter((r) => isPrivileged(r.role));
  if (!privileged.length) return <span className="muted">None</span>;
  if (isFuture(m.financial_authority_active_at, now)) {
    return (
      <StatusPill tone="info" icon="clock">
        Tenure pending until {formatLocal(m.financial_authority_active_at!)}
      </StatusPill>
    );
  }
  return <StatusPill tone="success">Active</StatusPill>;
}

function PendingCell({ m }: { m: OrgMember }) {
  const p = m.pending_role_request;
  if (!p) return <span className="muted">—</span>;
  return (
    <span className="small">
      {p.action_type === "ownership_transfer" ? "Becoming owner" : `${ROLE_LABEL[p.role]} requested`} (
      {p.status === "approved" ? "approved" : "awaiting approval"}); effective no earlier than {formatLocal(p.effective_at)}
      {p.single_user_fallback ? "; extended verification" : ""}
    </span>
  );
}

export function MembersPage() {
  const ep = useEndpoints();
  const session = useSession();
  const home = homeFor(session.partyType);
  const manager = isManager(session.me.roles);
  const owner = session.me.roles.includes("owner");
  const members = useQuery({ queryKey: ["org", "members"], queryFn: () => ep.org.listMembers() });
  const [changing, setChanging] = useState<OrgMember | null>(null);
  const [removing, setRemoving] = useState<OrgMember | null>(null);
  const [now] = useState(() => Date.now());

  return (
    <div className="stack-lg">
      <PageHeader title="Members" description="Everyone with access to this organization, their role and when they were last active." />
      <OrgTabs home={home} />
      <Card title="Members and roles">
        <QueryView query={members} loadingLabel="Loading members" lines={4}>
          {(data) =>
            data.members.length === 0 ? (
              <EmptyState icon="users" title="No members">
                Nobody has access yet.
              </EmptyState>
            ) : (
              <Table
                caption="Organization members"
                captionHidden
                rows={data.members}
                rowKey={(m) => m.user_id}
                columns={[
                  {
                    key: "member",
                    header: "Member",
                    cell: (m) => (
                      <span className="stack-sm">
                        <span className="break-all">{m.email}</span>
                        {m.user_id === session.me.user_id ? <Badge>You</Badge> : null}
                      </span>
                    ),
                  },
                  {
                    key: "role",
                    header: "Role",
                    cell: (m) => (
                      <span>
                        {roleLabel(m.role)}
                        {m.roles.length > 1 ? (
                          <span className="xs muted"> (also {m.roles.slice(1).map((r) => ROLE_LABEL[r.role]).join(", ")})</span>
                        ) : null}
                      </span>
                    ),
                  },
                  { key: "authority", header: "Financial authority", cell: (m) => <AuthorityCell m={m} now={now} /> },
                  { key: "pending", header: "Pending change", cell: (m) => <PendingCell m={m} /> },
                  {
                    key: "active",
                    header: "Last active",
                    cell: (m) => (m.last_active_at ? formatLocal(m.last_active_at) : <span className="muted">Never signed in</span>),
                  },
                  {
                    key: "mfa",
                    header: "Two-step",
                    cell: (m) => (m.mfa_enrolled ? "Set up" : <StatusPill tone="warning">Not set up</StatusPill>),
                  },
                  ...(manager
                    ? [
                        {
                          key: "actions",
                          header: "Actions",
                          cell: (m: OrgMember) =>
                            m.user_id === session.me.user_id ? (
                              <span className="muted xs">—</span>
                            ) : (
                              <span className="row">
                                <Button size="sm" variant="secondary" onClick={() => setChanging(m)} aria-label={`Change role for ${m.email}`}>
                                  Change role
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setRemoving(m)} aria-label={`Remove ${m.email}`}>
                                  Remove
                                </Button>
                              </span>
                            ),
                        },
                      ]
                    : []),
                ]}
              />
            )
          }
        </QueryView>
        <p className="xs muted">
          Today these roles are enforced on organization actions, approvals and payout-destination requests; campaigns, sites and keys
          don't check them yet.
        </p>
      </Card>

      <RoleMatrixCard />

      {manager ? <InviteCard home={home} /> : null}
      {manager ? <InvitationsCard home={home} /> : null}
      {owner && members.data ? <OwnershipTransferCard home={home} members={members.data.members} selfId={session.me.user_id} /> : null}
      {!manager ? (
        <p className="small muted">Only owners and admins can invite, remove or change the role of members.</p>
      ) : null}

      {changing ? <ChangeRoleDialog member={changing} home={home} onClose={() => setChanging(null)} /> : null}
      {removing && members.data ? (
        <RemoveMemberDialog member={removing} members={members.data.members} home={home} onClose={() => setRemoving(null)} />
      ) : null}
    </div>
  );
}

function RoleMatrixCard() {
  return (
    <Card title="What each role can do" subtitle="A fixed, published set of roles (UX-DESIGN.md §4.2). Owner, admin and the two approver roles are privileged.">
      <dl className="definition-list">
        {ROLES.map((r) => (
          <div key={r} style={{ display: "contents" }}>
            <dt>{ROLE_LABEL[r]}</dt>
            <dd>{ROLE_DESCRIPTION[r]}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

// ---- invite ------------------------------------------------------------------

function InviteCard({ home }: { home: string }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole | "">("");
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; role?: string }>({});
  const [sent, setSent] = useState<OrgInvitation | null>(null);
  const m = useMutation({
    mutationFn: (req: { email: string; role: OrgRole }) =>
      ep.org.invite(req.email, req.role, { idempotencyKey: intent.keyFor(JSON.stringify(req)) }),
    onSuccess: (res) => {
      intent.reset();
      setSent(res);
      setEmail("");
      setRole("");
      void qc.invalidateQueries({ queryKey: ["org", "invitations"] });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const fe: typeof fieldErrors = {};
    const addr = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) fe.email = "Enter the email address to invite.";
    if (!role) fe.role = "Choose a role.";
    setFieldErrors(fe);
    if (Object.keys(fe).length || !role) return;
    setSent(null);
    m.mutate({ email: addr, role });
  }

  return (
    <Card title="Invite a member" subtitle="The invitation email carries a link that lasts 7 days. You can revoke it before it's accepted.">
      <form className="stack" onSubmit={submit} noValidate>
        <Input label="Email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} error={fieldErrors.email} />
        <Select
          label="Role"
          value={role}
          placeholder="Choose a role"
          options={roleOptions}
          onChange={(e) => setRole(e.target.value as OrgRole)}
          error={fieldErrors.role}
          hint={role ? ROLE_DESCRIPTION[role] : undefined}
        />
        {role && isPrivileged(role) ? (
          <Banner tone="info" title="Privileged role: second approval needed">
            {PRIVILEGED_ROLE_TEXT} They join as a Member, and the {ROLE_LABEL[role]} role is requested for them when they accept.
          </Banner>
        ) : null}
        {m.isError ? (
          <CommandError error={m.error}>
            <RejectionDetail error={m.error} home={home} />
          </CommandError>
        ) : null}
        {sent ? (
          <Banner tone="success" title="Invitation sent" live="polite">
            {sent.email} is invited as {ROLE_LABEL[sent.role]}. The link expires {formatLocal(sent.expires_at)}.
            {sent.requires_dual_control ? " The role will need a second approval once they accept." : ""}
          </Banner>
        ) : null}
        <div>
          <Button type="submit" loading={m.isPending}>
            Send invitation
          </Button>
        </div>
      </form>
    </Card>
  );
}

const INVITATION_STATE: Record<OrgInvitation["state"], { label: string; tone: "info" | "neutral" | "success" | "warning" }> = {
  open: { label: "Open", tone: "info" },
  expired: { label: "Expired", tone: "neutral" },
  accepted: { label: "Accepted", tone: "success" },
  revoked: { label: "Revoked", tone: "neutral" },
};

function InvitationsCard({ home }: { home: string }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["org", "invitations"], queryFn: () => ep.org.listInvitations() });
  const intent = useIntentKey();
  const revoke = useMutation({
    mutationFn: (id: string) => ep.org.revokeInvitation(id, { idempotencyKey: intent.keyFor(id) }),
    onSuccess: () => {
      intent.reset();
      void qc.invalidateQueries({ queryKey: ["org", "invitations"] });
    },
  });
  return (
    <Card title="Invitations" subtitle="Invitations that haven't been accepted or revoked.">
      <div className="stack">
        {revoke.isError ? (
          <CommandError error={revoke.error}>
            <RejectionDetail error={revoke.error} home={home} />
          </CommandError>
        ) : null}
        <QueryView query={q} loadingLabel="Loading invitations">
          {(data) =>
            data.invitations.length === 0 ? (
              <p className="muted small">No open invitations.</p>
            ) : (
              <Table
                caption="Open invitations"
                captionHidden
                rows={data.invitations}
                rowKey={(i) => i.invitation_id}
                columns={[
                  { key: "email", header: "Email", cell: (i) => <span className="break-all">{i.email}</span> },
                  {
                    key: "role",
                    header: "Role",
                    cell: (i) => (
                      <span>
                        {ROLE_LABEL[i.role]}
                        {i.requires_dual_control ? <span className="xs muted"> (needs second approval)</span> : null}
                      </span>
                    ),
                  },
                  { key: "state", header: "State", cell: (i) => <StatusPill tone={INVITATION_STATE[i.state].tone}>{INVITATION_STATE[i.state].label}</StatusPill> },
                  { key: "expires", header: "Expires", cell: (i) => formatLocal(i.expires_at) },
                  {
                    key: "revoke",
                    header: "",
                    cell: (i) =>
                      i.state === "open" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={revoke.isPending && revoke.variables === i.invitation_id}
                          onClick={() => revoke.mutate(i.invitation_id)}
                          aria-label={`Revoke invitation for ${i.email}`}
                        >
                          Revoke
                        </Button>
                      ) : null,
                  },
                ]}
              />
            )
          }
        </QueryView>
      </div>
    </Card>
  );
}

// ---- role change ----------------------------------------------------------------

function ChangeRoleDialog({ member, home, onClose }: { member: OrgMember; home: string; onClose: () => void }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const [role, setRole] = useState<OrgRole | "">(member.role ?? "");
  const [done, setDone] = useState<ChangeRoleResponse | null>(null);
  const m = useMutation({
    mutationFn: (r: OrgRole) => ep.org.changeRole(member.user_id, r, { idempotencyKey: intent.keyFor(`${member.user_id}:${r}`) }),
    onSuccess: (res) => {
      intent.reset();
      setDone(res);
      void qc.invalidateQueries({ queryKey: ["org"] });
    },
  });
  const heldRoles = member.roles.map((r) => r.role);
  const grant = role !== "" && isPrivileged(role) && !heldRoles.includes(role) && !heldRoles.includes("owner");

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!m.isPending}
      title={`Change role: ${member.email}`}
      actions={
        done ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={m.isPending}>
              Cancel
            </Button>
            <Button onClick={() => role && m.mutate(role)} loading={m.isPending} disabled={!role}>
              {grant ? "Request role change" : "Change role"}
            </Button>
          </>
        )
      }
    >
      <div className="stack">
        {done ? (
          done.result === "dual_control_pending" && done.request ? (
            <Banner tone="info" title="Pending approval" live="polite">
              <PendingRequestNote request={done.request} subject={member.email} />
              <Link to={`${home}/org/approvals`}>Go to pending approvals</Link>
            </Banner>
          ) : done.result === "role_unchanged" ? (
            <Banner tone="neutral" live="polite">
              {member.email} already has exactly that role. Nothing changed.
            </Banner>
          ) : (
            <Banner tone="success" live="polite">
              {member.email} is now {roleLabel(done.member.role)}.
            </Banner>
          )
        ) : (
          <>
            <p className="small">
              Current role: <strong>{roleLabel(member.role)}</strong>. A member holds exactly one role after the change.
            </p>
            <Select
              label="New role"
              value={role}
              options={roleOptions}
              onChange={(e) => setRole(e.target.value as OrgRole)}
              hint={role ? ROLE_DESCRIPTION[role] : undefined}
            />
            {grant ? <Banner tone="info" title="This grant needs a second approver">{PRIVILEGED_ROLE_TEXT}</Banner> : null}
            {m.isError ? (
              <CommandError error={m.error}>
                <RejectionDetail error={m.error} home={home} />
              </CommandError>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

// ---- removal ---------------------------------------------------------------------

interface KeyChoice {
  action: "revoke" | "reassign" | "";
  to: string;
}

function RemoveMemberDialog({ member, members, home, onClose }: { member: OrgMember; members: OrgMember[]; home: string; onClose: () => void }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  // The keys come from the server's own refusal (api_key_disposition_required
  // {api_key_ids}, 0062:1504-1509): it is the only complete list of what
  // this member still has live.
  const [keyIds, setKeyIds] = useState<string[]>([]);
  const [choices, setChoices] = useState<Record<string, KeyChoice>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState<RemoveMemberResponse | null>(null);
  const others = members.filter((o) => o.user_id !== member.user_id);

  const m = useMutation({
    mutationFn: (disp: ApiKeyDisposition[]) =>
      ep.org.removeMember(member.user_id, disp, { idempotencyKey: intent.keyFor(`${member.user_id}:${JSON.stringify(disp)}`) }),
    onSuccess: (res) => {
      intent.reset();
      setDone(res);
      void qc.invalidateQueries({ queryKey: ["org"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "api_key_disposition_required") {
        const ids = Array.isArray(err.detail?.api_key_ids) ? err.detail.api_key_ids.filter((x): x is string => typeof x === "string") : [];
        setKeyIds(ids);
        setChoices((prev) => Object.fromEntries(ids.map((id) => [id, prev[id] ?? { action: "", to: "" }])));
      }
    },
  });

  function submit() {
    setFormError(null);
    const disp: ApiKeyDisposition[] = [];
    for (const id of keyIds) {
      const c = choices[id];
      if (!c || !c.action) return setFormError("Choose what happens to every API key.");
      if (c.action === "reassign") {
        if (!c.to) return setFormError("Choose who each reassigned key goes to.");
        disp.push({ api_key_id: id, action: "reassign", to_user_id: c.to });
      } else disp.push({ api_key_id: id, action: "revoke" });
    }
    m.mutate(disp);
  }

  const needsKeys = m.error instanceof ApiError && m.error.code === "api_key_disposition_required";

  return (
    <Dialog
      open
      alert
      onClose={onClose}
      dismissible={!m.isPending}
      title={`Remove ${member.email}?`}
      description="Removal takes effect at once: their sessions end, they can't sign in, their open invitations are revoked, and requests they made or that would promote them are canceled."
      actions={
        done ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={m.isPending}>
              Keep member
            </Button>
            <Button variant="danger" onClick={submit} loading={m.isPending}>
              Remove member
            </Button>
          </>
        )
      }
    >
      <div className="stack">
        {done ? (
          <Banner tone="success" title="Member removed" live="polite">
            {member.email} no longer has access. API keys revoked: {done.revoked_api_keys.length}; reassigned: {done.reassigned_api_keys.length};
            pending requests canceled: {done.canceled_requests.length}.
          </Banner>
        ) : (
          <>
            {keyIds.length > 0 ? (
              <div className="stack">
                <p className="small">
                  {member.email} created {keyIds.length === 1 ? "an API key that is" : `${keyIds.length} API keys that are`} still live. Choose
                  what happens to each: reassign it to another member (the key keeps working), or revoke it (any integration using it stops).
                </p>
                {keyIds.map((id) => {
                  const c = choices[id] ?? { action: "", to: "" };
                  return (
                    <div key={id} className="stack-sm">
                      <RadioGroup<"revoke" | "reassign">
                        legend={
                          <>
                            API key <code className="xs">{id}</code>
                          </>
                        }
                        name={`key-${id}`}
                        value={c.action}
                        onChange={(v) => setChoices((p) => ({ ...p, [id]: { ...c, action: v } }))}
                        options={[
                          { value: "reassign", label: "Reassign to another member" },
                          { value: "revoke", label: "Revoke it" },
                        ]}
                      />
                      {c.action === "reassign" ? (
                        <Select
                          label="Reassign to"
                          value={c.to}
                          placeholder="Choose a member"
                          options={others.map((o) => ({ value: o.user_id, label: o.email }))}
                          onChange={(e) => setChoices((p) => ({ ...p, [id]: { ...c, to: e.target.value } }))}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {formError ? (
              <Banner tone="error" live="alert">
                {formError}
              </Banner>
            ) : null}
            {m.isError ? (
              needsKeys ? (
                <Banner tone="warning" live="alert">
                  {m.error.message}
                </Banner>
              ) : (
                <CommandError error={m.error}>
                  <RejectionDetail error={m.error} home={home} />
                </CommandError>
              )
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

// ---- ownership transfer -------------------------------------------------------------

function OwnershipTransferCard({ home, members, selfId }: { home: string; members: OrgMember[]; selfId: string }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const intent = useIntentKey();
  const [to, setTo] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<OwnershipTransferResponse | null>(null);
  const candidates = members.filter((m) => m.user_id !== selfId && !m.roles.some((r) => r.role === "owner"));
  const target = members.find((m) => m.user_id === to);
  const mut = useMutation({
    mutationFn: (id: string) => ep.org.transferOwnership(id, { idempotencyKey: intent.keyFor(id) }),
    onSuccess: (res) => {
      intent.reset();
      setDone(res);
      setConfirming(false);
      void qc.invalidateQueries({ queryKey: ["org"] });
    },
    onError: () => setConfirming(false),
  });

  return (
    <Card
      title="Transfer ownership"
      subtitle="The only way for the last owner to step down. The new owner takes over after a second approval and the waiting period; you become an admin."
    >
      <div className="stack">
        {candidates.length === 0 ? (
          <p className="muted small">Invite another member first: there's nobody to transfer ownership to.</p>
        ) : (
          <>
            <Select
              label="New owner"
              value={to}
              placeholder="Choose a member"
              options={candidates.map((m) => ({ value: m.user_id, label: m.email }))}
              onChange={(e) => setTo(e.target.value)}
            />
            <div>
              <Button variant="secondary" disabled={!to} onClick={() => setConfirming(true)}>
                Transfer ownership…
              </Button>
            </div>
          </>
        )}
        {mut.isError ? (
          <CommandError error={mut.error}>
            <RejectionDetail error={mut.error} home={home} />
          </CommandError>
        ) : null}
        {done && target ? (
          <Banner tone="info" title="Ownership transfer requested" live="polite">
            <PendingRequestNote request={done.request} subject={target.email} />
          </Banner>
        ) : null}
      </div>
      <Dialog
        open={confirming}
        alert
        onClose={() => setConfirming(false)}
        dismissible={!mut.isPending}
        title={`Make ${target?.email ?? "this member"} the owner?`}
        description={PRIVILEGED_ROLE_TEXT}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={mut.isPending}>
              Cancel
            </Button>
            <Button onClick={() => mut.mutate(to)} loading={mut.isPending}>
              Request transfer
            </Button>
          </>
        }
      />
    </Card>
  );
}
